import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportAPI } from 'api/report';
import { IKpiChartOverlaysSettings, IReportOverlaysState } from '@report-content';

function parseStateIdFromPath(pathname: string): number {
    const match = pathname.match(/\/state\/(\d+)/);
    return match ? Number(match[1]) : 0;
}

function withStateIdInPath(pathname: string, stateId: number): string {
    const withoutState = pathname.replace(/\/state\/\d+/, '');
    return stateId ? `${withoutState}/state/${stateId}` : withoutState;
}

// Viewer-only: fetches the per-user, per-report, per-segment KPI Chart overlays state and keeps
// the `/state/{id}` URL segment in sync, the same way the metric viewer does for `/uco/{id}`.
// `syncUrl` must be false when embedded: the page URL then belongs to the host, not to this report.
export default function useReportOverlaysState(elementId: number, segmentValueId: number, syncUrl: boolean) {
    const [stateId, setStateId] = useState<number>(() =>
        syncUrl ? parseStateIdFromPath(window.location.pathname) : 0,
    );
    const stateIdRef = useRef(stateId);
    const segmentValueIdRef = useRef(segmentValueId);

    const [overlaysState, setOverlaysState] = useState<IReportOverlaysState>({});

    useEffect(() => {
        if (segmentValueIdRef.current !== segmentValueId) {
            segmentValueIdRef.current = segmentValueId;
            stateIdRef.current = 0;
            setStateId(0);
            setOverlaysState({});
        }
    }, [segmentValueId]);

    const { data } = useQuery({
        queryKey: ['report_overlays_state', elementId, segmentValueId],
        queryFn: () => reportAPI.getReportState(elementId, segmentValueId, stateIdRef.current),
        enabled: !!elementId,
    });

    const applyStateId = (id: number) => {
        if (!id || id === stateIdRef.current) {
            return;
        }
        stateIdRef.current = id;
        setStateId(id);
        if (syncUrl) {
            window.history.replaceState(null, document.title, withStateIdInPath(window.location.pathname, id));
        }
    };

    useEffect(() => {
        if (!data) {
            return;
        }
        applyStateId(data.id);
        setOverlaysState(data.state?.overlays ?? {});
    }, [data]);

    // Full report `state` is stored as a single JSON blob, so saving one block's overlays means
    // sending every block's overlays back, not just the changed one - otherwise the others get
    // wiped out by the server-side overwrite.
    const saveBlockOverlays = (blockUid: string, settings: IKpiChartOverlaysSettings) => {
        const nextOverlaysState = { ...overlaysState, [blockUid]: settings };
        setOverlaysState(nextOverlaysState);
        reportAPI
            .saveReportState(elementId, segmentValueId, { overlays: nextOverlaysState })
            .then((id) => applyStateId(id))
            .catch(() => {});
    };

    return { stateId, overlaysState, saveBlockOverlays };
}
